import React, { useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import {
  FiHome,
  FiUsers,
  FiLogOut,
  FiMenu,
  FiX,
  FiShield
} from 'react-icons/fi';

const LayoutContainer = styled.div`
  display: flex;
  height: 100vh;
  background-color: #f5f5f5;
`;

const Sidebar = styled.div<{ $isOpen: boolean }>`
  width: 280px;
  background-color: #1a1a2e;
  color: white;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease;
  position: fixed;
  height: 100%;
  z-index: 1000;
  
  @media (max-width: 768px) {
    transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
  }
`;

const Logo = styled.div`
  padding: 2rem;
  font-size: 1.5rem;
  font-weight: bold;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #ff6b6b;
  }
`;

const Nav = styled.nav`
  flex: 1;
  padding: 1rem 0;
`;

const NavItem = styled(Link)<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 2rem;
  color: ${props => props.$active ? '#fff' : 'rgba(255, 255, 255, 0.7)'};
  text-decoration: none;
  transition: all 0.2s;
  background-color: ${props => props.$active ? 'rgba(255, 107, 107, 0.2)' : 'transparent'};
  border-left: 3px solid ${props => props.$active ? '#ff6b6b' : 'transparent'};
  
  &:hover {
    color: white;
    background-color: rgba(255, 255, 255, 0.05);
  }
  
  svg {
    font-size: 1.2rem;
  }
`;

const UserInfo = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  .name {
    font-weight: 500;
    margin-bottom: 0.25rem;
  }
  
  .role {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.6);
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 1rem 2rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  font-size: 1rem;
  text-align: left;
  transition: all 0.2s;
  
  &:hover {
    color: white;
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const MainContent = styled.div`
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const Header = styled.header`
  background-color: white;
  padding: 1rem 2rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  h1 {
    font-size: 1.5rem;
    color: #333;
    margin: 0;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #333;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Content = styled.main`
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  
  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
`;

const menuItems = [
  { path: '/superadmin', label: 'Dashboard', icon: FiHome, exact: true },
  { path: '/superadmin/tenants', label: 'Tenants', icon: FiUsers },
];

const SuperadminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getPageTitle = () => {
    const currentItem = menuItems.find(item => {
      if (item.exact) {
        return location.pathname === item.path;
      }
      return location.pathname.startsWith(item.path);
    });
    return currentItem?.label || 'Superadmin';
  };

  return (
    <LayoutContainer>
      <Overlay $isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      
      <Sidebar $isOpen={sidebarOpen}>
        <Logo>
          <FiShield />
          Superadmin
        </Logo>
        
        <Nav>
          {menuItems.map(item => {
            const Icon = item.icon;
            const isActive = item.exact 
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path);
            
            return (
              <NavItem
                key={item.path}
                to={item.path}
                $active={isActive}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon />
                {item.label}
              </NavItem>
            );
          })}
        </Nav>
        
        <UserInfo>
          <div className="name">{user?.full_name || 'Superadmin'}</div>
          <div className="role">Superusuário</div>
        </UserInfo>
        
        <LogoutButton onClick={handleLogout}>
          <FiLogOut />
          Sair
        </LogoutButton>
      </Sidebar>
      
      <MainContent>
        <Header>
          <MobileMenuButton onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <FiX /> : <FiMenu />}
          </MobileMenuButton>
          <h1>{getPageTitle()}</h1>
        </Header>
        
        <Content>
          <Outlet />
        </Content>
      </MainContent>
    </LayoutContainer>
  );
};

export default SuperadminLayout;